import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { api } from '@/lib/api';

interface MinisterRow {
  id: string;
  memberName: string;
  title: string;
  role: string;
  isActive: boolean;
}

const guestMinisterId = (name: string) => `guest:${name}`;

export interface MinisterComboboxProps {
  id?: string;
  value: string;
  onChange: (name: string) => void;
  enabled?: boolean;
  placeholder: string;
  emptyMessage: string;
  guestLabel: (name: string) => string;
  clearLabel: string;
  popoverPortal?: PopoverPortalTarget;
}

export const MinisterCombobox = ({
  id,
  value,
  onChange,
  enabled = true,
  placeholder,
  emptyMessage,
  guestLabel,
  clearLabel,
  popoverPortal = 'body',
}: MinisterComboboxProps) => {
  const [ministerId, setMinisterId] = useState<string | null>(null);
  const [selected, setSelected] = useState<EntityOption | null>(null);
  const [query, setQuery] = useState('');

  const ministers = useQuery({
    queryKey: ['people', 'ministers', 'picker'],
    queryFn: () => api.get<{ data: MinisterRow[] }>('/people/ministers?pageSize=100'),
    enabled,
  });

  const ministerLabel = (row: MinisterRow) => `${row.title} ${row.memberName}`.trim();

  const applyTypedName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const match = (ministers.data?.data ?? []).find(
      (row) => ministerLabel(row).toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) {
      const option = { id: match.id, label: ministerLabel(match), description: match.role };
      setMinisterId(match.id);
      setSelected(option);
      setQuery('');
      onChange(option.label);
      return;
    }
    const option = { id: guestMinisterId(trimmed), label: trimmed };
    setMinisterId(option.id);
    setSelected(option);
    setQuery('');
    onChange(trimmed);
  };

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (ministers.data?.data ?? [])
      .filter((row) => row.isActive)
      .filter((row) => {
        if (!needle) return true;
        return `${row.title} ${row.memberName} ${row.role}`.toLowerCase().includes(needle);
      })
      .map((row) => ({
        id: row.id,
        label: ministerLabel(row),
        description: row.role,
      }));
  }, [ministers.data?.data, query]);

  useEffect(() => {
    if (!enabled) return;
    const trimmed = value.trim();
    if (!trimmed) {
      setMinisterId(null);
      setSelected(null);
      return;
    }
    if (selected?.label === trimmed) return;

    const match = (ministers.data?.data ?? []).find((row) => ministerLabel(row) === trimmed);
    if (match) {
      setMinisterId(match.id);
      setSelected({
        id: match.id,
        label: trimmed,
        description: match.role,
      });
      return;
    }

    setMinisterId(guestMinisterId(trimmed));
    setSelected({ id: guestMinisterId(trimmed), label: trimmed });
  }, [enabled, value, ministers.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EntityCombobox
      id={id}
      value={ministerId}
      onChange={(nextId) => {
        if (!nextId) {
          setMinisterId(null);
          setSelected(null);
          onChange('');
          return;
        }
        const option =
          options.find((row) => row.id === nextId) ??
          (selected?.id === nextId ? selected : null);
        if (!option) return;
        setMinisterId(nextId);
        setSelected(option);
        onChange(option.label);
      }}
      options={options}
      selected={selected}
      onQueryChange={setQuery}
      loading={ministers.isPending && !ministers.data}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      clearLabel={clearLabel}
      popoverPortal={popoverPortal}
      onQueryDismiss={applyTypedName}
      createAction={
        query.trim() &&
        !options.some((option) => option.label.toLowerCase() === query.trim().toLowerCase())
          ? {
              label: guestLabel(query.trim()),
              onClick: applyTypedName,
            }
          : undefined
      }
    />
  );
};
